import { useEffect, useRef } from 'react';
import { brandingApi } from '../api/brandingApi';
import { firmendatenApi } from '../api/firmendatenApi';
import { triggerBrandingUpdate } from '../hooks/useBranding';
import { triggerAuthChange } from '../hooks/useFirmendaten';
import { useAuth } from '../context/AuthContext';
import { refreshTitleWithCount } from '../context/NotificationContext';
import { getHostBranding } from '../utils/hostBranding';

// Hilfsfunktion zum Anpassen der Farbe
const adjustColor = (hex, percent, opacity = 1) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));

  if (opacity < 1) {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
};

// Font-Kategorien für Fallback
const FONT_CATEGORIES = {
  'Instrument Serif': 'serif',
  'Inter': 'sans-serif',
  'Roboto': 'sans-serif',
  'Open Sans': 'sans-serif',
  'Lato': 'sans-serif',
  'Montserrat': 'sans-serif',
  'Poppins': 'sans-serif',
  'Playfair Display': 'serif',
  'Merriweather': 'serif'
};

// Standard Google Fonts URL generieren
const generateGoogleFontsUrl = (fontName) => {
  if (!fontName || fontName === 'Montserrat') {
    return null;
  }
  const formattedName = fontName.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${formattedName}:wght@400;500;600;700&display=swap`;
};

// Font-Kategorie ermitteln
const getFontCategory = (fontName) => {
  return FONT_CATEGORIES[fontName] || 'sans-serif';
};

// Favicon dynamisch aktualisieren (verwendet das generierte 48x48 Favicon)
const updateFavicon = (faviconPath) => {
  const faviconLink = document.querySelector('link[rel="icon"]');

  if (!faviconLink) {
    console.warn('Favicon link element not found');
    return;
  }

  if (faviconPath) {
    // Organisation hat ein Favicon - verwenden
    // faviconPath enthält bereits /api/uploads/branding/...
    // API_BASE_URL entfernt /api um korrekte URL zu konstruieren
    const API_BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
    const faviconUrl = `${API_BASE_URL}${faviconPath}`;

    // Nur aktualisieren wenn sich die URL geändert hat
    if (faviconLink.href !== faviconUrl) {
      console.log('Favicon aktualisiert auf Organisations-Favicon:', faviconUrl);
      faviconLink.href = faviconUrl;
    }
  } else {
    // Kein Favicon - Standard-Favicon verwenden
    const defaultFavicon = '/favicon.png';
    if (!faviconLink.href.endsWith(defaultFavicon)) {
      console.log('Favicon zurückgesetzt auf Standard');
      faviconLink.href = defaultFavicon;
    }
  }
};

// Branding-Cache leeren (bei User-Wechsel)
export const clearBrandingCache = () => {
  console.log('Clearing branding cache...');
  localStorage.removeItem('branding-logo');
  localStorage.removeItem('branding-chaticon');
  localStorage.removeItem('branding-accent');
  // Favicon auf Standard zurücksetzen
  updateFavicon(null);
  triggerBrandingUpdate();
  triggerAuthChange();
};

// Branding-Einstellungen anwenden (CSS-Variablen setzen)
const applyBrandingSettings = (settings) => {
  console.log('Wende Branding an:', settings.accentColor, settings.headingFont, 'Logo:', settings.logoPath, 'Favicon:', settings.faviconPath);

  // Akzentfarbe setzen
  if (settings.accentColor) {
    document.documentElement.style.setProperty('--primary', settings.accentColor);
    document.documentElement.style.setProperty('--primary-dark', adjustColor(settings.accentColor, -15));
    document.documentElement.style.setProperty('--primary-light', adjustColor(settings.accentColor, 200, 0.15));
    // Für das Färben des Standard-Logos im useBranding-Hook persistieren
    localStorage.setItem('branding-accent', settings.accentColor);
  } else {
    localStorage.removeItem('branding-accent');
  }

  // Schriftart für Überschriften laden
  if (settings.headingFont && settings.headingFont !== 'Montserrat') {
    const fontUrl = generateGoogleFontsUrl(settings.headingFont);
    const fontCategory = getFontCategory(settings.headingFont);

    if (fontUrl) {
      const existingLink = document.getElementById('branding-font');
      if (existingLink) {
        existingLink.remove();
      }

      const link = document.createElement('link');
      link.id = 'branding-font';
      link.rel = 'stylesheet';
      link.href = fontUrl;
      document.head.appendChild(link);

      document.documentElement.style.setProperty('--heading-font', `'${settings.headingFont}', ${fontCategory}`);
    }
  } else {
    document.documentElement.style.setProperty('--heading-font', "'Montserrat', sans-serif");
  }

  // Logo-Pfad im LocalStorage speichern
  if (settings.logoPath) {
    localStorage.setItem('branding-logo', settings.logoPath);
  } else {
    localStorage.removeItem('branding-logo');
  }

  // Chat-Icon-Pfad im LocalStorage speichern
  if (settings.chatIconPath) {
    localStorage.setItem('branding-chaticon', settings.chatIconPath);
  } else {
    localStorage.removeItem('branding-chaticon');
  }

  // Favicon dynamisch aktualisieren (48x48 optimiertes Favicon oder Standard)
  updateFavicon(settings.faviconPath);

  triggerBrandingUpdate();
};

// Branding laden - exportiert für manuelle Aufrufe
export const loadBrandingSettings = async () => {
  const token = localStorage.getItem('token');

  try {
    let settings;

    if (token) {
      // Mit Token: Authentifizierter Request
      console.log('Lade Branding mit Auth-Token...');
      settings = await brandingApi.get();
    } else {
      // Ohne Token: Public-Route (Defaults)
      console.log('Lade Public-Branding (kein Token)...');
      // Bei Logout erst Cache leeren
      localStorage.removeItem('branding-logo');
      settings = await brandingApi.getPublic();
    }

    applyBrandingSettings(settings);
    return settings;
  } catch (error) {
    console.error('Branding laden fehlgeschlagen:', error);
    // Bei Fehler: Defaults setzen
    localStorage.removeItem('branding-logo');
    triggerBrandingUpdate();
  }
};

// Alias für Kompatibilität
export const reloadBranding = loadBrandingSettings;

function BrandingLoader() {
  const { user, loading } = useAuth();
  const lastUserIdRef = useRef(null);
  const lastOrgIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  // Initial-Load: Sofort beim ersten Render wenn Token vorhanden
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      const token = localStorage.getItem('token');

      if (token) {
        console.log('BrandingLoader: Initial-Load mit Token');
        loadBrandingSettings();
      } else {
        console.log('BrandingLoader: Kein Token bei Initial-Load, lösche Cache');
        clearBrandingCache();
      }
    }
  }, []);

  // Reagiere auf User-Änderungen (Login/Logout)
  useEffect(() => {
    if (loading) {
      return; // Warte bis Auth geladen ist
    }

    const currentUserId = user?.id || null;
    const currentOrgId = user?.organisation?.id || null;

    // Prüfe ob User oder Organisation sich geändert hat
    const userChanged = lastUserIdRef.current !== currentUserId;
    const orgChanged = lastOrgIdRef.current !== currentOrgId;

    if (userChanged || orgChanged) {
      const previousUserId = lastUserIdRef.current;
      lastUserIdRef.current = currentUserId;
      lastOrgIdRef.current = currentOrgId;

      // Bei jedem Wechsel: Erst Cache leeren, dann neu laden
      if (previousUserId !== null || currentUserId !== null) {
        console.log('BrandingLoader: User/Org geändert, lösche Cache und lade neu');
        clearBrandingCache();
        loadBrandingSettings();
      }
    }
  }, [user, loading]);

  // Firmendaten laden für Document Title (bei User-Änderung)
  useEffect(() => {
    if (loading) return;

    const loadFirmendaten = async () => {
      try {
        const token = localStorage.getItem('token');
        // Mit Token authentifizierte Route nutzen, sonst public
        const data = token
          ? await firmendatenApi.get()
          : await firmendatenApi.getPublic();

        // Embed-Mode: Im iFrame (HeyKiki-Portal o.ä.) keinen "- WerkPilot"-
        // Suffix anhängen, damit der Titel im Accessibility-Tree, in Dev-Tools
        // und beim "Frame in neuem Tab öffnen" white-label bleibt.
        const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
        const isEmbedParam = new URLSearchParams(window.location.search).get('embed') === '1';
        const isEmbed = isInIframe || isEmbedParam;

        const hb = getHostBranding();
        const brandSuffix = hb ? hb.name : 'WerkPilot';
        if (data?.firmenname) {
          document.title = isEmbed ? data.firmenname : `${data.firmenname} - ${brandSuffix}`;
        } else {
          document.title = isEmbed ? '' : brandSuffix;
        }
        // Unread-Count wieder zum Titel hinzufügen falls vorhanden
        refreshTitleWithCount();
      } catch (error) {
        console.error('Firmendaten laden fehlgeschlagen:', error);
        const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
        const isEmbedParam = new URLSearchParams(window.location.search).get('embed') === '1';
        const hbErr = getHostBranding();
        document.title = (isInIframe || isEmbedParam) ? '' : (hbErr ? hbErr.name : 'WerkPilot');
        refreshTitleWithCount();
      }
    };

    loadFirmendaten();
  }, [user, loading]);

  return null;
}

export default BrandingLoader;
